import {
  AgendaItemType,
  GetNoticeForPowerOfAttorneyRecipientResponse,
  GetNoticeForShareholderResponse,
  ReceivedPowerOfAttorney,
  RequestPageReload,
} from "./generalMeetingDomain";
import AlertMessageStore from "../../stores/AlertMessageStore";
import {
  AdminChangedMeetingChair,
  AdminMeetingAnnouncement,
  AdminUpdatedMeetingStatus,
  AdminVoted,
  ReceivedPoAForParticipant,
  RevokedPoAForParticipant,
  ShareholderGeneralMeetingMessage,
  ShareholderRegisteredAttendance,
  SubscriptionContext,
  TopicName,
} from "../../webSocketDomain";
import { action, runInAction } from "mobx";
import {
  mapToAttendanceAlertMessage,
  mapToElectronicGeneralMeetingAlertMessage,
  mapToReceivedPoAAlertMessage,
  mapToRevokedPoAAlertMessage,
  mapToVotingAlertMessage,
} from "./electronicGeneralMeetingDomain";
import { ToMeetingTabMessage } from "./generalMeetingComponents";
import { logDebug } from "../../utils/log";

import { EventDispatcher } from "../../events/eventsDomain";

function mapToCommonParticipantTopic(orgNo: string): TopicName {
  return `/topic/${orgNo}/shareholder-live-general-meeting`;
}

function mapToParticipantTopics(orgNo: string, noticeForShareholderIds: string[]): TopicName[] {
  return [
    mapToCommonParticipantTopic(orgNo),
    ...mapToDirectToParticipantTopics(noticeForShareholderIds),
  ];
}

function mapToDirectToParticipantTopics(noticeForShareholderIds: string[]): TopicName[] {
  return noticeForShareholderIds.map<TopicName>(
    (noticeForShareholderId) =>
      `/user/anonymous-${noticeForShareholderId}/shareholder-live-general-meeting`,
  );
}

export function subscribeToGeneralMeetingParticipantMessages({
  orgNo,
  noticeForShareholderIds,
  projectId,
  dispatch,
  onMessage,
}: {
  orgNo: string;
  noticeForShareholderIds: string[];
  projectId: string;
  dispatch: EventDispatcher;
  onMessage: (message: ShareholderGeneralMeetingMessage) => void;
}): () => void {
  const participantTopics: TopicName[] = mapToParticipantTopics(orgNo, noticeForShareholderIds);
  const subscriptions: SubscriptionContext[] = [];
  participantTopics.forEach((topic) => {
    dispatch([
      {
        topic,
        type: "subscribe-to-web-socket-messages",
        onMessage: (message) => {
          logDebug("participant received message:", message);
          if (projectId === message.projectId) {
            onMessage(message as ShareholderGeneralMeetingMessage);
          }
        },
        onSubscribed: (s) => subscriptions.push(s),
      },
    ]);
  });
  return () => subscriptions.forEach((s) => s.unsubscribe());
}

export function onMessage({
  response,
  messageStore,
  openAgendaItem,
  dispatch,
  updateSelectedAttendances,
  onLinkExpired,
  onRequestPageReload,
}: {
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  messageStore: AlertMessageStore;
  openAgendaItem: (item?: AgendaItemType) => void;
  dispatch: EventDispatcher;
  updateSelectedAttendances: () => void;
  onLinkExpired?: () => void;
  onRequestPageReload?: (requestPageReload: RequestPageReload) => void;
}) {
  return (message: ShareholderGeneralMeetingMessage) => {
    switch (message.type) {
      case "meeting-status-updated":
        handleMeetingStatusUpdate(response, messageStore, openAgendaItem)(message);
        break;
      case "meeting-chair-changed":
        handleMeetingChairChanged(response)(message);
        break;
      case "meeting-announcement":
        handleMeetingAnnouncement(onRequestPageReload)(message);
        break;
      case "shareholder-participation":
        handleParticipationUpdate(
          response,
          messageStore,
          openAgendaItem,
          updateSelectedAttendances,
        )(message);
        break;
      case "admin-voted":
        handleVotingUpdate(response, messageStore, openAgendaItem)(message);
        break;
      case "received-poa-for-participant":
        handleReceivedPoAForParticipant(
          response,
          messageStore,
          updateSelectedAttendances,
          onRequestPageReload,
        )(message);
        break;
      case "revoked-poa-for-participant":
        handleRevokedPoAForParticipant(response, messageStore, dispatch, onLinkExpired)(message);
        break;
    }
  };
}

function handleMeetingChairChanged(
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
) {
  return action((message: AdminChangedMeetingChair) => {
    response.chairOfMeeting = message.meetingChair;
    response.coSigners = message.coSigners;
  });
}

function handleMeetingAnnouncement(
  onRequestPageReload?: (requestPageReload: RequestPageReload) => void,
) {
  return action((message: AdminMeetingAnnouncement) => {
    onRequestPageReload?.({
      title: { key: "general-meeting.announcement-from-meeting-chair" },
      reason: { translated: message.announcement },
    });
  });
}

function handleMeetingStatusUpdate(
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  messageStore: AlertMessageStore,
  openAgendaItem: (item?: AgendaItemType) => void,
) {
  return action((message: AdminUpdatedMeetingStatus) => {
    const { statusName, status, decisionId, meeting } = message;
    response.electronicGeneralMeeting = meeting;
    const isAttending =
      response.shareholderParticipation?.find((p) => p.shareholderId === response.shareholderId)
        ?.isPresent ?? false;
    const alert = mapToElectronicGeneralMeetingAlertMessage({
      statusName,
      status,
      isAttending,
      response,
      decisionId,
    });

    messageStore.showMessage(
      alert.messageType,
      () => (
        <ToMeetingTabMessage
          message={alert.message}
          agenda={alert.openAgendaItem}
          openAgendaItem={openAgendaItem}
          linkText={alert.linkText}
        />
      ),
      { autoHide: false },
    );
  });
}

function handleParticipationUpdate(
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  messageStore: AlertMessageStore,
  openAgendaItem: (item?: AgendaItemType) => void,
  updateSelectedAttendances: () => void,
) {
  return (message: ShareholderRegisteredAttendance) =>
    runInAction(() => {
      const alert = mapToAttendanceAlertMessage({
        shareholderId: message.shareholderId,
        projectId: message.projectId,
        participation: message.participation,
        response,
        updateSelectedAttendances,
      });
      if (alert !== undefined) {
        messageStore.showMessage(
          alert.messageType,
          () => (
            <ToMeetingTabMessage
              message={alert.message}
              agenda={alert.openAgendaItem}
              openAgendaItem={openAgendaItem}
              linkText={alert.linkText}
            />
          ),
          { autoHide: false },
        );
      }
    });
}

function handleVotingUpdate(
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  messageStore: AlertMessageStore,
  openAgendaItem: (item?: AgendaItemType) => void,
) {
  return (message: AdminVoted) =>
    runInAction(() => {
      const { shareholderId, projectId, voteType, decisionId, participation } = message;
      const alert = mapToVotingAlertMessage({
        shareholderId,
        projectId,
        voteType,
        decisionId,
        participation,
        response,
      });
      if (alert !== undefined) {
        messageStore.showInfo(
          () => (
            <ToMeetingTabMessage
              message={alert.message}
              agenda={alert.openAgendaItem}
              openAgendaItem={openAgendaItem}
              linkText={alert.linkText}
            />
          ),
          { autoHide: false },
        );
      }
    });
}

function handleReceivedPoAForParticipant(
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  messageStore: AlertMessageStore,
  updateSelectedAttendances: () => void,
  onRequestPageReload?: (requestPageReload: RequestPageReload) => void,
) {
  return (message: ReceivedPoAForParticipant) =>
    runInAction(() => {
      const alert = mapToReceivedPoAAlertMessage({
        projectId: message.projectId,
        receivedPowerOfAttorneys: message.receivedPowerOfAttorneys,
        name: message.name,
        response,
        updateSelectedAttendances,
      });
      if (alert) {
        messageStore.showInfo(alert.message, { autoHide: true });
        onRequestPageReload?.({
          title: { key: "poa.page-reload-required" },
          reason: { key: "poa.received-reason" },
        });
      }
    });
}

function handleRevokedPoAForParticipant(
  response: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  messageStore: AlertMessageStore,
  dispatch: EventDispatcher,
  onLinkExpired?: () => void,
) {
  return (message: RevokedPoAForParticipant) =>
    runInAction(() => {
      const alert = mapToRevokedPoAAlertMessage({
        projectId: message.projectId,
        revokedNoticeId: message.revokedNoticeId,
        revokerName: message.revokerName,
        response,
      });
      if (alert) {
        messageStore.showInfo(alert.message, { autoHide: true });
      }
      const isShareholder = "shareholderId" in response && response.shareholderId !== undefined;
      if (!isShareholder) {
        dispatch([
          {
            type: "unsubscribe-from-web-socket-messages",
            topics: mapToDirectToParticipantTopics([message.revokedNoticeId]),
          },
        ]);
      }
      if (isLinkExpired(message, response.receivedPowerOfAttorneys) && !isShareholder) {
        onLinkExpired?.();
        dispatch([
          {
            type: "unsubscribe-from-web-socket-messages",
            topics: [mapToCommonParticipantTopic(response.orgNo)],
          },
        ]);
      }
    });
}

export const isLinkExpired = (
  message: RevokedPoAForParticipant,
  poas: ReceivedPowerOfAttorney[],
): boolean => message.revokedNoticeId !== undefined && poas.length === 0;
